import { useState } from 'react'
import { api, errorMessage } from '../api/client'
import type { SaleView } from '../api/types'
import { SaleByType } from '../components/SaleByType'
import { Amt, Card, ConfirmDelete, Dt, Empty, Note, NumberCell, Wt } from '../components/ui'
import { useApi } from '../hooks/useApi'
import { formatWeight } from '../lib/format'

/**
 * The selling screen, and the last few entries so a slip can be put right where
 * it was made.
 *
 * <p>The form itself lives in {@link SaleByType}, because the same form sits at
 * the top of روزانہ بکری - that is the page the shop is on all morning, and the
 * two must not drift apart.
 *
 * <p>There is no payment box on this form and there never will be. Goods going
 * out and money coming in are separate events on separate days; a form that
 * takes both at once invites a half-remembered figure into the wrong one.
 */
export function NewSale() {
  const recent = useApi<SaleView[]>('/sales/recent?limit=12')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  async function reprice(id: string, weight: string, rate: string) {
    setError(null)
    try {
      await api.put(`/sales/${id}`, { weight, rate })
      recent.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  async function remove(id: string) {
    setError(null)
    try {
      await api.delete(`/sales/${id}`)
      setDone('سودا ڈیلیٹ ہو گیا')
      recent.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const rows = recent.data ?? []
  const totalWeight = rows.reduce((sum, row) => sum + Number(row.weight), 0)

  return (
    <>
      {/*
        No hint under the title any more. It talked somebody through a form of
        identical labelled boxes, where the order was not obvious; the form now
        opens with the choice it wants first, as a switch nobody can miss, and
        then asks for three things in the order they are answered. A paragraph
        explaining a screen that explains itself is just something to read past
        every morning.
      */}
      <SaleByType title="نیا سودا" onSaved={() => recent.reload()} />

      <Card title="پچھلے سودے">
        {error && <Note kind="error">{error}</Note>}
        {done && <Note kind="info">{done}</Note>}

        <p className="muted small" style={{ marginTop: 0 }}>
          غلطی سے لکھی گئی انٹری یہیں ٹھیک کریں یا ڈیلیٹ کر دیں۔
        </p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th className="col-extra">تاریخ</th>
                <th>نقد / گاہک</th>
                <th className="col-extra">چیز</th>
                <th>وزن</th>
                <th>ریٹ</th>
                <th>رقم</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((sale) => (
                <tr key={sale.id}>
                  <td className="col-extra">
                    <Dt value={sale.date} />
                  </td>
                  <td className="name-cell">
                    {sale.cash ? <span className="muted">نقد</span> : sale.customerName}
                  </td>
                  <td className="col-extra">{sale.itemName}</td>
                  <td className="num">
                    <NumberCell
                      ariaLabel="وزن"
                      value={sale.weight}
                      decimals
                      width={90}
                      onCommit={(value) => void reprice(sale.id, value, sale.rate)}
                    />
                  </td>
                  <td className="num">
                    <NumberCell
                      ariaLabel="ریٹ"
                      value={sale.rate}
                      width={90}
                      onCommit={(value) => void reprice(sale.id, sale.weight, value)}
                    />
                  </td>
                  <td className="num">
                    <strong>
                      <Amt value={sale.amount} />
                    </strong>
                  </td>
                  <td>
                    <ConfirmDelete onConfirm={() => void remove(sale.id)} />
                  </td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <Empty>ابھی کوئی سودا نہیں لکھا گیا۔</Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {rows.length > 0 && (
          <p className="muted small" style={{ marginTop: 10 }}>
            آخری <span className="amt">{rows.length}</span> انٹریاں دکھائی جا رہی ہیں۔ پورا حساب
            «کھاتہ» میں دیکھیں۔ کل وزن <Wt value={formatWeight(totalWeight)} />۔
          </p>
        )}
      </Card>
    </>
  )
}
